import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2, Pause, Play } from 'lucide-react';

import { reportsAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 3.3): review real
// calls — list → one call's turn-by-turn timings, what was said, events, and
// (admins) the recorded audio. Backend: /advanced-reports/call-center/calls*.

const toDateString = (d: Date) => d.toISOString().split('T')[0];
const secs = (ms?: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);
const when = (iso?: string | null) => (iso ? new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString() : '—');
const PER_PAGE = 25;

function reasonBadge(reason?: string | null) {
  if (!reason) return <Badge variant="outline">live</Badge>;
  const bad = ['failed', 'idle_timeout'].includes(reason);
  return <Badge variant={bad ? 'destructive' : 'outline'}>{reason.replace(/_/g, ' ')}</Badge>;
}

function CallDetail({ callId, onBack }: { callId: number; onBack: () => void }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    reportsAPI.getCallCenterCall(callId).then(setData).catch((e: any) => setError(e?.message || 'Failed to load'));
    return () => audioRef.current?.pause();
  }, [callId]);

  const play = async (name: string) => {
    if (playing === name) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    setAudioError(null);
    try {
      const blob = await reportsAPI.getCallCenterCallAudio(callId, name);
      audioRef.current?.pause();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlaying(null);
        URL.revokeObjectURL(url);
      };
      setPlaying(name);
      await audio.play();
    } catch (e: any) {
      setPlaying(null);
      setAudioError(e?.response?.status === 403 ? 'Only admins can listen to call audio.' : 'Could not play this file.');
    }
  };

  if (error) return <p className="p-6 text-sm text-destructive">{error}</p>;
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading call…
      </div>
    );
  }
  const c = data.call;
  const t = data.totals || {};

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" className="gap-1" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" /> All calls
      </Button>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Started', when(c.started_at)],
          ['Caller', c.caller],
          ['Duration', secs(c.duration_ms)],
          ['Ended', c.end_reason ? c.end_reason.replace(/_/g, ' ') : 'live'],
          ['Turns', t.turns ?? '—'],
          ['Avg first reply', secs(t.avg_first_reply_ms)],
          ['Interruptions', t.barge_ins ?? '—'],
          ['Providers', `${c.stt_provider || '?'} / ${c.tts_provider || '?'}`],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded-xl border border-border/60 p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="truncate text-sm font-medium tabular-nums">{value as any}</p>
          </div>
        ))}
      </div>

      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Turn by turn</CardTitle></CardHeader>
        <CardContent>
          {data.turns.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Silence wait</TableHead>
                  <TableHead>Speech-to-text</TableHead>
                  <TableHead>AI first word</TableHead>
                  <TableHead>First reply heard</TableHead>
                  <TableHead>STT / voice</TableHead>
                  <TableHead>Language</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.turns.map((turn: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{turn.turn_id ?? i + 1}</TableCell>
                    <TableCell className="tabular-nums">{secs(turn.endpointing_ms)}</TableCell>
                    <TableCell className="tabular-nums">{secs(turn.stt_latency_ms)}</TableCell>
                    <TableCell className="tabular-nums">{secs(turn.llm_first_token_ms)}</TableCell>
                    <TableCell className="tabular-nums font-medium">{secs(turn.meaningful_answer_ms)}</TableCell>
                    <TableCell className="font-mono text-xs">{turn.stt_provider || '?'} / {turn.tts_provider || '?'}</TableCell>
                    <TableCell>{turn.stt_language || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No answered turns recorded.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">What was said</CardTitle></CardHeader>
          <CardContent className="max-h-96 space-y-2 overflow-y-auto">
            {data.transcript.length ? (
              data.transcript.map((m: any, i: number) => (
                <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m.speaker === 'caller' ? 'bg-muted' : 'bg-accent/40'}`}>
                  <p className="text-[11px] text-muted-foreground">{m.speaker === 'caller' ? 'Caller' : 'AI'} · {when(m.at)}</p>
                  <p>{m.text}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Nothing saved for this call.</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Recorded audio</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {audioError && <p className="text-xs text-destructive">{audioError}</p>}
              {data.audio.length ? (
                data.audio.map((a: any) => (
                  <div key={a.name} className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => play(a.name)} aria-label={`Play ${a.name}`}>
                      {playing === a.name ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <span className="text-xs">{a.kind}</span>
                    <span className="truncate font-mono text-[11px] text-muted-foreground">{a.name}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No audio kept for this call.</p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Events</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              {data.events.map((e: any, i: number) => (
                <div key={i} className="flex gap-2">
                  <span className="text-muted-foreground">{when(e.at)}</span>
                  <span className="font-medium">{e.type.replace(/_/g, ' ')}</span>
                  {e.detail && <span className="truncate text-muted-foreground">{e.detail}</span>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function CallReview({ dateRange }: { dateRange: { startDate: Date; endDate: Date } }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    reportsAPI
      .getCallCenterCalls({
        start_date: toDateString(dateRange.startDate),
        end_date: toDateString(dateRange.endDate),
        page,
        per_page: PER_PAGE,
      })
      .then((d: any) => !cancelled && setData(d))
      .catch(() => !cancelled && setData({ total: 0, calls: [] }));
    return () => {
      cancelled = true;
    };
  }, [dateRange.startDate, dateRange.endDate, page]);

  if (selected !== null) return <CallDetail callId={selected} onBack={() => setSelected(null)} />;
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading calls…
      </div>
    );
  }
  const pages = Math.max(1, Math.ceil((data.total || 0) / PER_PAGE));

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Calls ({data.total})</CardTitle>
      </CardHeader>
      <CardContent>
        {data.calls.length ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Caller</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Turns</TableHead>
                  <TableHead>Avg first reply</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Ended</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.calls.map((c: any) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelected(c.id)}>
                    <TableCell className="whitespace-nowrap">{when(c.started_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{c.caller}</TableCell>
                    <TableCell className="tabular-nums">{secs(c.duration_ms)}</TableCell>
                    <TableCell className="tabular-nums">{c.turns ?? '—'}</TableCell>
                    <TableCell className="tabular-nums">{secs(c.avg_first_reply_ms)}</TableCell>
                    <TableCell>{c.language || '—'}</TableCell>
                    <TableCell>{reasonBadge(c.end_reason)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pages > 1 && (
              <div className="mt-3 flex items-center justify-end gap-2 text-xs">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <span>Page {page} of {pages}</span>
                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No calls in this period.</p>
        )}
      </CardContent>
    </Card>
  );
}
